import { faBoxOpen, faPowerOff, faUser } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Skeleton } from '@shared/components/ui/shadcn/skeleton';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { PackingStation } from '../../models/PackingStation';

const pickNumber = (source: Record<string, unknown>, keys: string[]): number | undefined => {
  for (const key of keys) {
    const value = source?.[key];
    if (typeof value === 'number') return value;
    if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
      return Number(value);
    }
  }
  return undefined;
};

/** Suma de relaciones cliente por puesto (misma lógica que la columna/tarjeta de clientes). */
const sumClientsCount = (stations: PackingStation[]): number => {
  return stations.reduce((acc, station) => {
    const raw = station as Record<string, unknown>;
    const clients = Array.isArray(raw.clients) ? raw.clients : [];
    const count = pickNumber(raw, ['clients_count', 'clientsCount']) ?? clients.length;
    return acc + count;
  }, 0);
};

export interface PackingStationsStatsProps {
  /** Filas cargadas en la tabla (mismas que recibe la grilla / lista). */
  packingStations: PackingStation[];
  isLoading?: boolean;
  className?: string;
}

/**
 * Bloques de estadísticas (mismo patrón visual que OrdersShipped → Stats).
 * Los valores se derivan de los datos mostrados en la lista.
 */
export const PackingStationsStats = ({
  packingStations,
  isLoading = false,
  className = '',
}: PackingStationsStatsProps) => {
  const { t } = useTranslation('packingStationsModule');

  const { totalStations, activeStations, clientsCount } = useMemo(() => {
    const totalStations = packingStations.length;
    const activeStations = packingStations.filter((s) => Boolean(s.active)).length;
    const clientsCount = sumClientsCount(packingStations);
    return { totalStations, activeStations, clientsCount };
  }, [packingStations]);

  const stats = [
    {
      title: t('stats.totalStations'),
      value: totalStations,
      icon: faBoxOpen,
      iconColor: 'text-info',
    },
    {
      title: t('stats.activeStations'),
      value: activeStations,
      icon: faPowerOff,
      iconColor: 'text-success',
    },
    {
      title: t('stats.clients'),
      value: clientsCount,
      icon: faUser,
      iconColor: 'text-icons',
    },
  ];

  return (
    <>
      {/* Stats grid - mismo layout que OrdersShipped (3 columnas en desktop) */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6 ${className}`}>
        {stats.map((stat, index) => (
          <div key={index} className="border border-card-border rounded-lg p-4 min-h-[84px]">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-card-bg flex items-center justify-center flex-shrink-0">
                {isLoading ? (
                  <Skeleton className="w-3 h-3 rounded-sm" />
                ) : (
                  <FontAwesomeIcon icon={stat.icon} className={`w-3.5 h-3.5 ${stat.iconColor}`} />
                )}
              </div>
              <div className="flex flex-col min-w-0">
                {isLoading ? (
                  <>
                    <Skeleton className="h-4 w-28 mb-1" />
                    <Skeleton className="h-5 w-16" />
                  </>
                ) : (
                  <>
                    <h3 className="text-sm font-medium text-complementary-text mb-1 uppercase tracking-wide">
                      {stat.title}
                    </h3>
                    <div className="text-base font-semibold text-title tabular-nums">{stat.value}</div>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};
